import { useCallback, useState, type ChangeEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { validateLoa } from '@equip/utils';
import type { SF1034DocumentPayload } from '@/services/iem/types';
import { useRestrictedProgramLabels } from '@/hooks/useRestrictedProgramLabels';

/** A cart item passed to the dialog for per-item LOA collection. */
export interface RestrictedProgramDialogCartItem {
  id: string;
  name: string;
  /** Pre-populated LOA from the catalog entry (may be overridden by the user). */
  defaultLoa?: string;
}

export interface RestrictedProgramCompleteIssueDialogProps {
  open: boolean;
  /** Called when the user dismisses or clicks "Return to Cart" */
  onClose: () => void;
  /**
   * Called when the user confirms the form.
   * `payload` contains the collected payee / LOA fields.
   */
  onConfirm: (payload: SF1034DocumentPayload) => void;
  /**
   * Called when the user chooses "Skip Document".
   * The issue is still completed but without an SF 1034.
   */
  onSkip: () => void;
  /** Whether the issue completion is currently in progress */
  isSubmitting?: boolean;
  /** Pre-populated SQD/Section (from customer.unit) */
  defaultSquadronSection?: string;
  /** Pre-populated Accountable Officer name (current comptroller user) */
  defaultAccountableOfficer?: string;
  /** Pre-populated Certifying Officer name (restricted (buy) program) */
  defaultCertifyingOfficer?: string;
  /** Pre-populated SSN from customer record */
  defaultSsn?: string;
  /** Restricted (buy) program cart items — one LOA input is rendered per item. */
  cartItems?: RestrictedProgramDialogCartItem[];
  /** Whether a signature has been captured or explicitly skipped */
  signatureAcknowledged?: boolean;
  /** Data URL of the captured signature, if any */
  signaturePreviewSrc?: string;
  /** Called to open the signature capture flow */
  onCaptureSignature?: () => void;
  /** Called to clear a previously captured signature */
  onClearSignature?: () => void;
  /** Called to explicitly skip signature capture */
  onSkipSignature?: () => void;
}

/**
 * Restricted (buy) program — "Complete Issue - SF 1034" dialog.
 *
 * Collects Payee Information (SSN, SQD/Section, Routing #, Account #, Email),
 * Accountable Officer, and Line of Accounting (LOA) before completing the issue.
 *
 * This is intentionally separate from `DocumentFieldsModal` (the Buy Program
 * popup that drives a dynamic backend preview).  The restricted (buy) program
 * form is a static, known set of fields.
 */
export const RestrictedProgramCompleteIssueDialog = ({
  open,
  onClose,
  onConfirm,
  onSkip,
  isSubmitting = false,
  defaultSquadronSection = '',
  defaultAccountableOfficer = '',
  defaultCertifyingOfficer = '',
  defaultSsn = '',
  cartItems = [],
  signatureAcknowledged,
  signaturePreviewSrc,
  onCaptureSignature,
  onClearSignature,
  onSkipSignature,
}: RestrictedProgramCompleteIssueDialogProps) => {
  const { programLabel } = useRestrictedProgramLabels();
  const [ssn, setSsn] = useState(defaultSsn);
  const [squadronSection, setSquadronSection] = useState(defaultSquadronSection);
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountableOfficer, setAccountableOfficer] = useState(defaultAccountableOfficer);
  const [certifyingOfficer, setCertifyingOfficer] = useState(defaultCertifyingOfficer);
  const [loa, setLoa] = useState('');
  // Routing Number and Account Number are required together whenever this SF 1034
  // is being created (#1079). Tracked separately so each field shows its own
  // inline error instead of only a submit-time error.
  const [routingNumberError, setRoutingNumberError] = useState(false);
  const [accountNumberError, setAccountNumberError] = useState(false);
  // LOA format is validated inline so an invalid value is caught here — within
  // the popup, without losing any entered data — instead of only surfacing as a
  // server error after the popup has already closed (#1078).
  const [loaError, setLoaError] = useState<string | null>(null);

  const handleRoutingNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setRoutingNumber(e.target.value);
    if (e.target.value.trim()) setRoutingNumberError(false);
  }, []);

  const handleAccountNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAccountNumber(e.target.value);
    if (e.target.value.trim()) setAccountNumberError(false);
  }, []);

  const handleLoaChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setLoa(e.target.value);
    setLoaError(null);
  }, []);

  const handleConfirm = useCallback(() => {
    const routingMissing = !routingNumber.trim();
    const accountMissing = !accountNumber.trim();
    setRoutingNumberError(routingMissing);
    setAccountNumberError(accountMissing);

    const loaValidationError = validateLoa(loa);
    setLoaError(loaValidationError ?? null);

    if (routingMissing || accountMissing || loaValidationError) {
      return;
    }

    const payload: SF1034DocumentPayload = {
      payeeSsn: ssn.trim() || undefined,
      payeeSquadronSection: squadronSection.trim() || undefined,
      payeeRoutingNumber: routingNumber.trim() || undefined,
      payeeAccountNumber: accountNumber.trim() || undefined,
      accountableOfficer: accountableOfficer.trim() || undefined,
      certifyingOfficer: certifyingOfficer.trim() || undefined,
      ...(loa.trim() &&
        cartItems.length > 0 && {
          itemLoaOverrides: Object.fromEntries(cartItems.map((item) => [item.id, loa.trim()])),
        }),
    };
    onConfirm(payload);
  }, [
    ssn,
    squadronSection,
    routingNumber,
    accountNumber,
    accountableOfficer,
    certifyingOfficer,
    loa,
    cartItems,
    onConfirm,
  ]);

  // Reset form state when dialog re-opens
  const handleEntered = useCallback(() => {
    setSsn(defaultSsn);
    setSquadronSection(defaultSquadronSection);
    setRoutingNumber('');
    setAccountNumber('');
    setRoutingNumberError(false);
    setAccountNumberError(false);
    setAccountableOfficer(defaultAccountableOfficer);
    setCertifyingOfficer(defaultCertifyingOfficer);
    // Pre-fill LOA from first cart item's defaultLoa if present
    setLoa(cartItems[0]?.defaultLoa ?? '');
    setLoaError(null);
  }, [
    defaultSsn,
    defaultSquadronSection,
    defaultAccountableOfficer,
    defaultCertifyingOfficer,
    cartItems,
  ]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEntered: handleEntered }}
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Complete Issue - SF 1034
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review the SF 1034 form details before completing the Buy Program issue. Required fields
            are marked with an asterisk (*). You can skip to upload your own document later.
          </Typography>
        </Box>
        {!isSubmitting && (
          <IconButton size="small" onClick={onClose} sx={{ ml: 1 }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Divider sx={{ mb: 2 }} />

        {/* Payee Information */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Payee Information
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="SSN *"
            placeholder="SSN"
            helperText="Individual SSN"
            fullWidth
            value={ssn}
            onChange={(e) => setSsn(e.target.value)}
            disabled={isSubmitting}
          />
          <TextField
            size="small"
            label="SQD/Section"
            helperText="Squadron or section"
            fullWidth
            value={squadronSection}
            onChange={(e) => setSquadronSection(e.target.value)}
            disabled={isSubmitting}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="Routing Number (ROUTING#) *"
            placeholder="Routing Number (ROUTING#)"
            helperText={
              routingNumberError ? 'Routing Number is required' : 'Bank routing number for payment'
            }
            error={routingNumberError}
            required
            fullWidth
            value={routingNumber}
            onChange={handleRoutingNumberChange}
            disabled={isSubmitting}
          />
          <TextField
            size="small"
            label="Account Number (ACCT#) *"
            placeholder="Account Number (ACCT#)"
            helperText={
              accountNumberError
                ? 'Account Number is required'
                : 'Bank account number - appears in Payee Name/Address block'
            }
            error={accountNumberError}
            required
            fullWidth
            value={accountNumber}
            onChange={handleAccountNumberChange}
            disabled={isSubmitting}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Accountable Officer */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Accountable Officer
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="Accountable Officer"
          placeholder="Accountable Officer"
          helperText="Accountable Officer assigned to this issue"
          value={accountableOfficer}
          onChange={(e) => setAccountableOfficer(e.target.value)}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Certifying Officer */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Certifying Officer
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="Certifying Officer"
          placeholder="Certifying Officer"
          helperText={`Certifying Officer — additional ${programLabel} signature`}
          value={certifyingOfficer}
          onChange={(e) => setCertifyingOfficer(e.target.value)}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* LOA — single field for the whole transaction */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          LOA (Line of Accounting)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Enter the LOA for this issue transaction.
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="LOA"
          placeholder="LOA"
          helperText={loaError ?? undefined}
          error={!!loaError}
          value={loa}
          onChange={handleLoaChange}
          disabled={isSubmitting}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Signature */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Signature
        </Typography>
        <Stack spacing={0.5} sx={{ mb: 3 }}>
          <Button variant="outlined" fullWidth onClick={onCaptureSignature} disabled={isSubmitting}>
            {signatureAcknowledged && signaturePreviewSrc
              ? 'Signature Captured'
              : signatureAcknowledged
                ? 'Signature Skipped'
                : 'Capture Signature'}
          </Button>
          {!signatureAcknowledged && (
            <Button
              variant="text"
              size="small"
              fullWidth
              onClick={onSkipSignature}
              disabled={isSubmitting}
              sx={{ mt: -0.5 }}
            >
              Skip Signature
            </Button>
          )}
          {signaturePreviewSrc ? (
            <Stack spacing={0.5}>
              <Paper variant="outlined" sx={{ p: 1, bgcolor: '#ffffff' }}>
                <Box
                  component="img"
                  src={signaturePreviewSrc}
                  alt="Captured signature preview"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxHeight: 120,
                    objectFit: 'contain',
                  }}
                />
              </Paper>
              {onClearSignature ? (
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={onClearSignature}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Clear signature
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Return to Cart
        </Button>
        <Button
          variant="outlined"
          sx={{ color: 'var(--equip-success)', borderColor: 'var(--equip-success)' }}
          onClick={onSkip}
          disabled={isSubmitting || !signatureAcknowledged}
        >
          Skip Document
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isSubmitting || !signatureAcknowledged}
        >
          {isSubmitting ? 'Completing Issue…' : 'Create Document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
